import React, { useMemo } from 'react';
import { useRef, useState } from 'react';
import '../css/animate.css';
import '../css/barvy.css';
import '../css/style.css';
import {motion} from "framer-motion";
import { useEffect } from 'react';
import biker1 from "../images/biker1.jpg";
import biker2 from "../images/biker2.jpg";
import biker3 from "../images/biker3.jpg";
import { getLocalizedText } from '../utils/textLocalization';
import { getCurrentLanguage } from '../utils/languageSwitcher';
import { isEditryConfigured } from '../utils/editryConfig';
import {
  EditableContent,
  useEditry,
  usePreloadPublicData,
  usePublicContentList,
} from '@lazylabs-dev/editry-react';

const LOCALE_TO_CONTENT_LANGUAGE = {
  'cs-CZ': 'cs',
  'sk-SK': 'sk',
  'pl-PL': 'pl',
  'hu-HU': 'hu',
};

const getPreferredLanguages = (locale) => {
  const preferred = LOCALE_TO_CONTENT_LANGUAGE[locale] || 'cs';
  return preferred === 'cs' ? ['cs', 'default'] : [preferred, 'cs', 'default'];
};

const getEditryLanguage = (locale) => LOCALE_TO_CONTENT_LANGUAGE[locale] || 'cs';

const normalizeContentLanguage = (language) => {
  if (typeof language !== 'string') {
    return 'default';
  }

  const normalized = language.trim().toLowerCase();
  return normalized || 'default';
};

const getLocalizedContentValue = (contentList, contentKey, fallback = '', preferredLanguages = ['cs']) => {
  if (!Array.isArray(contentList)) {
    return fallback;
  }

  const records = contentList.filter((item) => item.content_key === contentKey);
  if (!records.length) {
    return fallback;
  }

  let record = null;
  for (const language of preferredLanguages) {
    const targetLanguage = normalizeContentLanguage(language);
    record = records.find((item) => normalizeContentLanguage(item.language) === targetLanguage);
    if (record) {
      break;
    }
  }

  if (!record) {
    record = records[0];
  }

  const value = record?.value;
  if (typeof value === 'string' && value.trim() !== '') {
    return value;
  }

  return fallback;
};

const parseAboutList = (rawValue) => {
  if (Array.isArray(rawValue)) {
    return rawValue;
  }

  if (typeof rawValue !== 'string' || !rawValue.trim()) {
    return [];
  }

  try {
    const parsed = JSON.parse(rawValue);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const FALLBACK_PARTS = [
  {
    obrazek: biker1,
    text: '<h2>O Nukeproof</h2><p>Obsah této sekce je možné upravit v Editry administraci.</p>'
  },
  {
    obrazek: biker2,
    text: '<h2>Technologie</h2><p>Doplňte text a obrázky pro historii a příběh značky.</p>'
  },
  {
    obrazek: biker3,
    text: '<h2>Komunita</h2><p>Spravujte jednotlivé bloky v klíči onas_onas_list.</p>'
  },
];

function App() {
  const locale = getCurrentLanguage();
  const preferredLanguages = useMemo(() => getPreferredLanguages(locale), [locale]);
  const editryLanguage = useMemo(() => getEditryLanguage(locale), [locale]);
  const editryReady = isEditryConfigured();
  const { overlayEnabled, adminSession } = useEditry();
  const isEditModeActive = overlayEnabled && Boolean(adminSession?.token);

  usePreloadPublicData({ contentKeys: ['onas_onas_nazev', 'onas_onas_hlavninazev', 'onas_onas_list'], auto: editryReady });
  const contentListResponse = usePublicContentList();

  const allContent = useMemo(
    () => contentListResponse.data?.data || [],
    [contentListResponse.data]
  );

  const parts = useMemo(() => {
    if (!editryReady) {
      return FALLBACK_PARTS;
    }

    const rawList = getLocalizedContentValue(allContent, 'onas_onas_list', '', preferredLanguages);
    const parsedList = parseAboutList(rawList)
      .filter((item) => item && (item.obrazek || item.text))
      .map((item) => ({
        obrazek: typeof item.obrazek === 'string' ? item.obrazek : '',
        text: typeof item.text === 'string' ? item.text : ''
      }));

    return parsedList.length ? parsedList : FALLBACK_PARTS;
  }, [allContent, editryReady, preferredLanguages]);

  const listFallbackJson = useMemo(() => JSON.stringify(parts, null, 2), [parts]);

  const sectionLabel = useMemo(() => {
    if (!editryReady) {
      return 'O Nukeproof';
    }

    return getLocalizedContentValue(allContent, 'onas_onas_nazev', 'O Nukeproof', preferredLanguages);
  }, [allContent, editryReady, preferredLanguages]);

  const sectionHeading = useMemo(() => {
    if (!editryReady) {
      return 'Historie';
    }

    return getLocalizedContentValue(allContent, 'onas_onas_hlavninazev', 'Historie', preferredLanguages);
  }, [allContent, editryReady, preferredLanguages]);
  
  useEffect(() => {
    document.title = getLocalizedText('general.o-nukeproof_page_title');
  }, []);


      const onasRef = useRef(null);

       const [timeline, setTimeline] = useState({
         top: 0,
         height: 0,
         left: 0
       });
     
       useEffect(() => {
         const updateTimeline = () => {
           if (!onasRef.current) return;

           const container = onasRef.current;
           const containerRect = container.getBoundingClientRect();

           const spans = container.querySelectorAll(".time-line");

           if (spans.length < 2) return;

           const first = spans[0].getBoundingClientRect();
           const last = spans[spans.length - 1].getBoundingClientRect();

           setTimeline({
             top: first.top - containerRect.top + first.height / 2,
             height: last.top - first.top,
             left: first.left - containerRect.left + first.width / 2
           });
         };
       
         // Po vykreslení
         updateTimeline();
       
         // Při změně velikosti okna
         window.addEventListener("resize", updateTimeline);
       
         return () => {
           window.removeEventListener("resize", updateTimeline);
         };
       }, [parts]);


  return (
    <>
    <motion.div  key="loading"initial={{ opacity: 0 }}animate={{ opacity: 1 }}exit={{ opacity: 0 }}transition={{ duration: 0.3 }}>
            <div id="o-nukeproof" className="o-nukeproof">
                <div className="section-heading">

                <div className="o-nukeproof-container">
                  <div className="onas-col-left">
                      <h3 className="barlow-semibold">
                        {isEditModeActive ? (
                          <EditableContent contentKey="onas_onas_nazev" language={editryLanguage} as="span" fallback={sectionLabel} showFallbackWhileLoading={false}/>
                        ) : (
                          sectionLabel
                        )}
                      </h3>
                      <h2 className="section-title onas-title white-title" id="about-nadpis">
                        {isEditModeActive ? (
                          <EditableContent contentKey="onas_onas_hlavninazev" language={editryLanguage} as="span" fallback={sectionHeading} showFallbackWhileLoading={false}/>
                        ) : (
                          sectionHeading
                        )}
                      </h2>

                      {isEditModeActive && (
                        <div className="onas-text" style={{ width: '100%', marginBottom: 20 }}>
                          <h3 className="barlow-semibold">Sekce (JSON)</h3>
                          <EditableContent contentKey="onas_onas_list" language={editryLanguage} as="pre" className="info-content" contentType="list" fallback={listFallbackJson} showFallbackWhileLoading={false}/>
                        </div>
                      )}
                    </div>
                  </div>



                  <div className="o-nukeproof-container">
                    <div ref={onasRef} className="onas-col-left onas-text">
                      <div className="timeline-line" style={{ top: `${timeline.top}px`, height: `${timeline.height}px`, left: `${timeline.left}px`}}/>

                      {parts.map((element, index) => {
                        const modifiedText = (element.text || "").replace(
                          /(<h2[\s>])/gi,
                          '<span class="time-line"></span>$1'
                        );
                        return (
                          <>
                              {element.obrazek ? (
                                <img src={element.obrazek || biker1} id="about-obrazek" alt="Nukeproof" className="onas-col-left-img"/>
                              ) : null}
                            <div
                              key={index}
                              className="html-unblock"
                              dangerouslySetInnerHTML={{
                                __html: modifiedText
                              }}
                            />
                          </>
                        );
                      })}

                      <div className="timeline-line-end" style={{ top: `${timeline.top + timeline.height}px`, left: `${timeline.left}px`}}/>
                    </div>


                    <div className="onas-col-right">
                      {parts.map((element, index) => (
                        element.obrazek ? (
                          <img key={index} alt="Nukeproof" src={element.obrazek || biker1}/>
                        ) : null
                      ))}
                    </div>
                  </div>



                </div>
            </div>
    </motion.div>
    </>
  );
}

/*
[text(HTML), obrazek(URL, string)]

text OBRAZEK(flip)


<div>
  <p />
  <img />
</div>
*/

export default App;